import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Warning,
  Trash,
  CheckCircle,
  ShieldWarning,
  Info,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHero from "../components/site/PageHero";
import { COMPANY } from "../data/site";

const reasons = [
  "I no longer use JayesPay",
  "I have switched to another platform",
  "I am closing my retail business",
  "Privacy / data concerns",
  "I am facing technical issues",
  "Other",
];

export default function AccountDeactivation() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    retailerId: "",
    reason: reasons[0],
    details: "",
  });
  const [confirm, setConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!confirm) return;
    setLoading(true);
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
    }, 700);
  };

  return (
    <div data-testid="deactivation-page">
      <PageHero
        eyebrow="Account Management"
        title="Account"
        highlight="Deactivation"
        description="We're sorry to see you go. Submit your request below and our team will permanently deactivate your JayesPay account along with associated data, in line with our Privacy Policy."
        testId="deactivation-hero"
      />

      <section className="py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {/* Notices */}
          <div className="space-y-3 mb-8">
            <div className="flex gap-3 items-start p-4 rounded-2xl bg-warnBg border border-warnBg/0">
              <Warning size={20} weight="fill" className="text-warnText flex-shrink-0 mt-0.5" />
              <div className="text-sm text-warnText">
                <strong>Heads up:</strong> Deactivation is permanent. Wallet
                balance, transaction history (subject to legal retention),
                package and commissions will no longer be accessible.
              </div>
            </div>
            <div className="flex gap-3 items-start p-4 rounded-2xl bg-infoBg">
              <Info size={20} weight="fill" className="text-infoText flex-shrink-0 mt-0.5" />
              <div className="text-sm text-infoText">
                Withdraw any remaining wallet balance and complete pending RD /
                BC actions before submitting this request.
              </div>
            </div>
          </div>

          <div className="bg-surfaceAlt border border-line rounded-3xl p-6 sm:p-10">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="deactivation-success-state"
                className="text-center py-10"
              >
                <div className="mx-auto h-20 w-20 rounded-full bg-errorBg flex items-center justify-center mb-5">
                  <CheckCircle size={40} weight="fill" className="text-errorText" />
                </div>
                <h3 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight mb-2">
                  Deactivation request received
                </h3>
                <p className="text-ink700 max-w-md mx-auto mb-6">
                  Your request has been submitted. Our team will verify and
                  process the deactivation. You&apos;ll receive a final
                  confirmation at the email on record.
                </p>
                <p className="text-xs text-ink500">
                  For urgent queries: {COMPANY.email} · {COMPANY.phone}
                </p>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-errorText mb-1">
                  <ShieldWarning size={14} weight="fill" />
                  Permanent deactivation
                </div>
                <h2 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight">
                  Submit your deactivation request.
                </h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="d-name" className="text-ink700 mb-1.5 block">
                      Full name (as on KYC)
                    </Label>
                    <Input
                      id="d-name"
                      data-testid="deact-name"
                      required
                      value={form.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      className="bg-surface border-line"
                    />
                  </div>
                  <div>
                    <Label htmlFor="d-id" className="text-ink700 mb-1.5 block">
                      Retailer ID (optional)
                    </Label>
                    <Input
                      id="d-id"
                      data-testid="deact-id"
                      value={form.retailerId}
                      onChange={(e) => onChange("retailerId", e.target.value)}
                      className="bg-surface border-line"
                      placeholder="VS-XXXXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="d-email" className="text-ink700 mb-1.5 block">
                      Registered email
                    </Label>
                    <Input
                      id="d-email"
                      data-testid="deact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => onChange("email", e.target.value)}
                      className="bg-surface border-line"
                    />
                  </div>
                  <div>
                    <Label htmlFor="d-mobile" className="text-ink700 mb-1.5 block">
                      Registered mobile
                    </Label>
                    <Input
                      id="d-mobile"
                      data-testid="deact-mobile"
                      type="tel"
                      required
                      value={form.mobile}
                      onChange={(e) => onChange("mobile", e.target.value)}
                      className="bg-surface border-line"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-ink700 mb-1.5 block">
                    Reason for deactivation
                  </Label>
                  <Select
                    value={form.reason}
                    onValueChange={(v) => onChange("reason", v)}
                  >
                    <SelectTrigger
                      data-testid="deact-reason"
                      className="bg-surface border-line"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {reasons.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`reason-${r}`}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="d-details" className="text-ink700 mb-1.5 block">
                    Additional details (optional)
                  </Label>
                  <Textarea
                    id="d-details"
                    data-testid="deact-details"
                    rows={4}
                    value={form.details}
                    onChange={(e) => onChange("details", e.target.value)}
                    className="bg-surface border-line resize-none"
                    placeholder="Help us understand. Your feedback shapes JayesPay."
                  />
                </div>

                <label className="flex items-start gap-3 p-4 rounded-2xl bg-errorBg/40 border border-errorBg/0 cursor-pointer">
                  <Checkbox
                    data-testid="deact-confirm"
                    checked={confirm}
                    onCheckedChange={(c) => setConfirm(Boolean(c))}
                    className="mt-0.5 border-errorText data-[state=checked]:bg-errorText data-[state=checked]:border-errorText"
                  />
                  <span className="text-sm text-errorText">
                    I understand that account deactivation is permanent and
                    that {COMPANY.name} will delete my personal data subject to
                    regulatory retention requirements.
                  </span>
                </label>

                <Button
                  type="submit"
                  data-testid="deact-submit"
                  disabled={!confirm || loading}
                  className="w-full bg-errorText text-surfaceAlt hover:bg-brandNavy rounded-full h-12 text-sm font-bold disabled:opacity-50"
                >
                  <Trash size={16} weight="bold" className="mr-2" />
                  {loading ? "Submitting..." : "Submit deactivation request"}
                </Button>

                <p className="text-xs text-ink500 text-center">
                  Need help instead?{" "}
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="text-infoText font-semibold"
                  >
                    Email support
                  </a>{" "}
                  before deactivating.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
